import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { CapacityProgress, LimitInput, TableCard } from '../styles';
import { PoleLimit } from '../types';
import { getStatus, occupancyPct } from '../utils';

interface PoleLimitsTableProps {
  poles: PoleLimit[];
  draftLimits: Record<number, string>;
  invalidPoleIds: number[];
  onLimitChange: (poleId: number, value: string) => void;
}

export function PoleLimitsTable({
  poles,
  draftLimits,
  invalidPoleIds,
  onLimitChange,
}: PoleLimitsTableProps): JSX.Element {
  return (
    <TableCard>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Polo</TableCell>
              <TableCell>Credenciados ativos</TableCell>
              <TableCell>Ocupação</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Limite do polo</TableCell>
              <TableCell>Vagas restantes</TableCell>
              <TableCell>Fila de espera</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {poles.map((pole) => {
              const draftLimit = Number(draftLimits[pole.id]);
              const hasError = invalidPoleIds.includes(pole.id);
              const displayLimit = hasError ? pole.limit : draftLimit;
              const occupancy = Math.min(
                occupancyPct(pole.activeAccreditations, displayLimit),
                100
              );
              let progressColor: 'error' | 'secondary' | 'warning' =
                'secondary';
              if (occupancy >= 100) {
                progressColor = 'error';
              } else if (occupancy >= 90) {
                progressColor = 'warning';
              }
              const status = getStatus(pole.activeAccreditations, displayLimit);

              return (
                <TableRow key={pole.id}>
                  <TableCell>
                    <Typography fontWeight={600}>{pole.name}</Typography>
                    <Typography color="text.secondary" fontSize={13}>
                      {pole.state}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>
                      {pole.activeAccreditations}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box
                      alignItems="center"
                      display="flex"
                      gap={1.5}
                      minWidth={180}
                    >
                      <CapacityProgress
                        color={progressColor}
                        value={occupancy}
                        variant="determinate"
                      />
                      <Typography color="text.secondary" fontSize={13}>
                        {Math.round(occupancy)}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      color={status.color}
                      label={status.label}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <LimitInput
                      error={hasError}
                      helperText={
                        hasError ? `Mínimo: ${pole.activeAccreditations}` : ' '
                      }
                      inputProps={{
                        'aria-label': `Limite de credenciados do polo ${pole.name}`,
                        min: pole.activeAccreditations,
                        step: 1,
                      }}
                      onChange={(event) =>
                        onLimitChange(pole.id, event.target.value)
                      }
                      size="small"
                      type="number"
                      value={draftLimits[pole.id]}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>
                      {Math.max(displayLimit - pole.activeAccreditations, 0)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>
                      {pole.waitlistCount}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </TableCard>
  );
}
